/**
 * StockPrimitives — small, stateless UI bits shared across all Stock tab components.
 * Kept in one file to avoid over-fragmenting; each export is ~5-15 lines.
 */
import React from 'react';
import { Zap, CheckCircle2, SkipForward, ChevronUp, ChevronDown } from 'lucide-react';
import { useRole } from '@/contexts/RoleContext';

// Reusable sortable column header — used by all stock tab tables
export const SortTh = ({ col, label, right = false, sub, sort, setSort, className = '' }) => {
  const active = sort.col === col;
  const Icon = active && sort.dir === 'desc' ? ChevronUp : ChevronDown;
  return (
    <th
      onClick={() => setSort(s => s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' })}
      className={`py-3 px-4 cursor-pointer select-none whitespace-nowrap ${right ? 'text-right' : 'text-left'} ${className}`}
    >
      <span className="inline-flex items-center gap-1">
        <span>{label}</span>
        <Icon className={`h-3 w-3 ${active ? 'text-primary' : 'text-muted-foreground/40'}`} />
      </span>
      {sub && <span className="block text-[10px] normal-case font-normal tracking-normal text-muted-foreground/70">{sub}</span>}
    </th>
  );
};

// Pack size + unit sub-line shown below every product name
export const PackMeta = ({ packSize, unit }) => {
  const parts = [packSize, unit].filter(Boolean);
  if (!parts.length) return null;
  return (
    <span className="text-[11px] text-muted-foreground/70 font-normal leading-none">
      {parts.join(' · ')}
    </span>
  );
};

// Urgency badge for expiry tab
export const UrgencyBadge = ({ urgency, daysLeft }) => {
  const MAP = {
    expired:  'bg-red-100 text-red-700',
    critical: 'bg-orange-100 text-orange-700',
    warning:  'bg-amber-100 text-amber-700',
    normal:   'bg-blue-100 text-blue-700',
    no_data:  'bg-gray-100 text-gray-500',
  };
  const label = urgency === 'expired'
    ? `Expired ${Math.abs(daysLeft)}d ago`
    : urgency === 'no_data' ? 'No sales data'
    : `${daysLeft}d left`;
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${MAP[urgency] || MAP.normal}`}>{label}</span>;
};

// Smart Action button — resolves action by catalog_id then name.
// Shows orange clickable button for open actions, disabled status pill for addressed/skipped.
// Hidden entirely for Starter tier users (Growth+ only).
export const SmartActionBtn = ({ catalogId, productName, openActions, addressedActions = [], skippedActions = [], onOpenAction }) => {
  const { canUseSmartActionsInStock } = useRole();
  if (!canUseSmartActionsInStock()) return null;

  const find = (list) => {
    const byId = list.find(a => a.catalog_id === catalogId);
    return byId || (productName
      ? list.find(a => (a.product_name || '').trim().toLowerCase() === productName.trim().toLowerCase())
      : null);
  };

  const openAction      = find(openActions);
  const addressedAction = !openAction && find(addressedActions);
  const skippedAction   = !openAction && !addressedAction && find(skippedActions);

  if (openAction) {
    return (
      <button
        onClick={() => onOpenAction(openAction)}
        className="flex items-center gap-1 text-xs border border-orange-300 text-orange-600 hover:bg-orange-50 px-2 py-1 rounded-lg transition-colors whitespace-nowrap"
      >
        <Zap className="h-3 w-3" />
        Smart Action
      </button>
    );
  }

  if (addressedAction) {
    return (
      <span className="flex items-center gap-1 text-xs border border-emerald-200 text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg whitespace-nowrap cursor-default select-none opacity-80">
        <CheckCircle2 className="h-3 w-3" />
        Addressed
      </span>
    );
  }

  if (skippedAction) {
    return (
      <span className="flex items-center gap-1 text-xs border border-gray-200 text-gray-500 bg-gray-50 px-2 py-1 rounded-lg whitespace-nowrap cursor-default select-none opacity-80">
        <SkipForward className="h-3 w-3" />
        Skipped
      </span>
    );
  }

  return null;
};
